import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  IconButton,
  Select,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Switch,
  Typography,
  Box,
  Chip,
  Card,
  Paper,
  TableContainer,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import * as Yup from "yup";
import masterroleApi from "../../../../services/managelicense";
import getalluserApi from "../../../../services/managelicense";
import { makeStyles } from "@mui/styles";
import CustomDatagrid from "../../../../components/datagrid";
import getallusermappingApi from "../../../../services/managelicense";
import getbusinessuuidApi from "../../../../services/managelicense";
import useridentityApi from "../../../../services/managelicense";
import UserRoleModal from "./create";
import RoleChartModal from "./role_chart";
import RoleCreation from "./role_creation";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../../redux/loader/actions";
import { showSnackbar } from "../../../../redux/snackbar/actions";
import {
  setEncryptedCookie,
  getDecryptedCookie,
  removeCookie,
} from "../../../../utils";
import { useParams } from "react-router-dom";

const useStyles = makeStyles({
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },
  },
  chip: {
    position: "absolute",
    top: 0,
    right: 0,
    margin: "-12px",
  },
  buttonContainer: {
    position: "relative",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-start",
  },
});

const UserRoleManager = ({ onPrevious, onNext }) => {
  const { productid, busUid, businesssuuid } = useParams();
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems?.find(
    (item) => item.name.toLowerCase() === "subscriptions"
  );
  const isDisabled = createMenuItem?.access_type === "read_write";
  const classes = useStyles();
  const dispatch = useDispatch();
  const proid = getDecryptedCookie("proid") || productid;
  const organizationItems = useSelector(
    (state) => state.organizationReducer.items
  );

  const [userData, setUserData] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [lockedRoles, setLockedRoles] = useState({});
  const [userRoles, setUserRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [userRoleManagerOpen, setUserRoleManagerOpen] = useState(false);
  const [roleChartModalOpen, setRoleChartModalOpen] = useState(false);
  const [customRoleModalOpen, setCustomRoleModalOpen] = useState(false);
  const [businessuuid, setBusinessuuid] = useState([]);
  const [getUserMapping, setGetUserMapping] = useState([]);
  const [userIdentity, setUserIdentity] = useState([]);
  const [getUserMappingData, setGetUserMappingData] = useState([]);
  const [mode, setMode] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [roleChart, setRoleChart] = useState();

  const businessid = getDecryptedCookie("bi") || busUid;
  const business_uuid = getDecryptedCookie("buslap") || businessuuid;
  const user_uuid = getDecryptedCookie("uuid");

  const fetchMasterRoles = async (proid) => {
    try {
      dispatch(showLoader());
      const res = await masterroleApi.getmasterrole(proid);
      dispatch(hideLoader());
      if (Array.isArray(res.data.data.data)) {
        setRolesData(res.data.data.data);
      } else {
        console.error("Fetched rolesData is not an array:", res.data.data);
      }
    } catch (err) {
      dispatch(hideLoader());
      console.error("Failed to fetch master roles", err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      dispatch(showLoader());
      const res = await getalluserApi.getalluser(proid);
      dispatch(hideLoader());
      setUserData(res.data.data);
    } catch (err) {
      dispatch(hideLoader());
      console.error("Failed to fetch users", err);
    }
  };

  const fetchBusinessUUID = async () => {
    try {
      const res = await getbusinessuuidApi.getbusinessId(proid, business_uuid);
      if (res.data.status) {
        setBusinessuuid(res.data.data.business_uuid);
      }
    } catch (err) {
      console.error("Failed to fetch business UUID", err);
    }
  };

  const fetchUserMapping = useCallback(
    async (businessid) => {
      try {
        dispatch(showLoader());
        const res = await getallusermappingApi.getallusermapping(
          proid,
          businessid,
          paginationModel.page,
          paginationModel.pageSize
        );
        dispatch(hideLoader());
        setGetUserMapping(res.data.data.data);
        setRowCount(res.data.data.total_count);
      } catch (err) {
        dispatch(hideLoader());
        console.error("Failed to fetch user mappings", err);
      }
    },
    [businessid, paginationModel]
  );

  const fetchRoleChart = async (proid) => {
    try {
      dispatch(showLoader());
      const res = await masterroleApi.getProRole(proid);
      const data = res.data.data.data;

      if (!Array.isArray(data)) {
        throw new Error("Invalid response: Expected an array");
      }
      setRoleChart(data);
    } catch (err) {
      dispatch(hideLoader());
      console.error("Failed to fetch role chart", err);
    } finally {
      dispatch(hideLoader());
    }
  };

  const fetchUserMappingData = async (proid, businessid, uuid) => {
    try {
      dispatch(showLoader());
      const res = await getallusermappingApi.getusermappingdata(
        proid,
        businessid,
        uuid
      );
      dispatch(hideLoader());
      setGetUserMappingData(res.data.data.data);
    } catch (err) {
      dispatch(hideLoader());
      console.error("Failed to fetch user mapping data", err);
    }
  };

  const handleSubmit = (values) => {
    dispatch(showLoader());
    const data = {
      role_id: values.selectedRole,
      sso_user_id: values.selectedUser,
      business_id: businessid,
      sso_business_id: null,
      ...(mode === "edit" && { is_lock: values.is_lock }),
    };

    const apiCall =
      mode === "edit" ? useridentityApi.userEdit : useridentityApi.useridentity;

    apiCall(proid, data)
      .then((res) => {
        setUserIdentity(res.data.data);
        formik.resetForm();
        fetchUserMapping(businessid);
        dispatch(hideLoader());
        if (mode === "edit") {
          dispatch(
            showSnackbar({
              message:
                res.data.data.message === "UserUpdated" && "User Updated",
              autoHideDuration: 3000,
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "success",
            })
          );
        } else {
          dispatch(
            showSnackbar({
              message: res?.data?.data.message,
              autoHideDuration: 3000,
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "success",
            })
          );
        }
        setMode();
      })
      .catch((err) => {
        console.error("Error in submitting user role", err);
        dispatch(hideLoader());
        const errorMessage =
          err?.response?.data?.res?.message ||
          err?.response?.data?.message ||
          "Something went wrong";

        dispatch(
          showSnackbar({
            message: errorMessage,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
      });
  };

  const formik = useFormik({
    initialValues: {
      selectedUser: "",
      selectedRole: "",
      is_lock: false,
    },
    validationSchema: Yup.object({
      selectedUser: Yup.string().required("User is required"),
      selectedRole: Yup.string().required("Role is required"),
    }),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleToggleLock = (params) => {
    const sso_user_id = params.row.sso_user_id;
    const is_locked = params.row.access === true ? false : true;
    setLockedRoles((prev) => ({
      ...prev,
      [params.row.sso_user_id]: is_locked,
    }));

    dispatch(showLoader());
    const data = {
      is_lock: is_locked,
      role_id: params.row.role_uuid,
      sso_user_id: sso_user_id,
      business_id: businessid,
    };
    useridentityApi
      .userEdit(proid, data)
      .then((res) => {
        dispatch(
          showSnackbar({
            message: res.data.data.message === "UserUpdated" && "User Updated",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        fetchUserMapping(businessid);
        setMode();
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error("Error:", err);
        const errorMessage =
          err?.response?.data?.res?.message ||
          err?.response?.data?.message ||
          "Something went wrong";

        dispatch(
          showSnackbar({
            message: errorMessage,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
      });
  };

  const handleEditClick = (uuid) => {
    setMode("edit");
    fetchUserMappingData(proid, businessid, uuid);
  };

  useEffect(() => {
    if (proid) {
      fetchMasterRoles(proid);
      fetchAllUsers(proid);
      fetchBusinessUUID(proid);
      fetchUserMapping(businessid);
      fetchRoleChart(proid);
    }
  }, [proid, fetchUserMapping, paginationModel, organizationItems]);

  useEffect(() => {
    if (mode === "edit" && getUserMappingData.length > 0) {
      formik.setFieldValue("selectedUser", getUserMappingData[0]?.sso_user_id);
      formik.setFieldValue("selectedRole", getUserMappingData[0]?.role_uuid);
    }
  }, [mode, getUserMappingData, organizationItems]);

  const UserListRows = Array.isArray(getUserMapping)
    ? getUserMapping.map((data, index) => ({
        id: index + 1,
        sno_id: index + 1,
        user: data?.username,
        role: data?.role,
        role_uuid: data?.role_uuid,
        user_login: data?.user_login_id,
        access: data?.access,
        uuid: data?.user_uuid,
        sso_user_id: data?.sso_user_id,
      }))
    : [];

  const columns = [
    {
      field: "sno_id",
      headerName: "S.No",
      width: 100,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "user",
      headerName: "User",
      width: 150,
      headerAlign: "left",
      align: "left",
      sortable: false,
    },
    {
      field: "role",
      headerName: "Role",
      width: 250,
      headerAlign: "left",
      align: "left",
      sortable: false,
    },
    {
      field: "user_login",
      headerName: "User Login ID",
      width: 200,
      headerAlign: "left",
      align: "left",
      sortable: false,
    },
    ...(isDisabled
      ? [
          {
            field: "access",
            headerName: "Unlock/Lock",
            width: 150,
            headerAlign: "left",
            align: "left",
            sortable: false,
            renderCell: (params) => {
              const userRole = params.row.role;
              const isSuperAdmin =
                userRole && userRole.toLowerCase() === "super admin";
              if (isSuperAdmin) {
                return null;
              }

              return (
                <Switch
                  checked={params.row.access === true}
                  onChange={() => handleToggleLock(params)}
                  disabled={!isDisabled}
                  inputProps={{ "aria-label": "Lock/Unlock switch" }}
                />
              );
            },
          },
        ]
      : [
          {
            field: "access",
            headerName: "Status",
            width: 150,
            headerAlign: "left",
            align: "center",
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
              return (
                <Chip
                  variant="outlined"
                  size="small"
                  label={params?.row?.access ? "Active" : "In-Active"}
                  color={params?.row?.access ? "success" : "error"}
                />
              );
            },
          },
        ]),
    ...(isDisabled
      ? [
          {
            field: isDisabled ? "action" : null,
            headerName: isDisabled ? "Action" : null,
            width: isDisabled ? 150 : null,
            headerAlign: "left",
            align: "left",
            sortable: false,
            renderCell: (params) => {
              const isSameUser = params.row.sso_user_id === user_uuid;

              return !isSameUser ? (
                isDisabled ? (
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handleEditClick(params.row.uuid)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                ) : null
              ) : null;
            },
          },
        ]
      : []),
  ];

  const handleCustomRoleSave = (roleName) => {
    setSelectedRole(roleName);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box className={classes.buttonContainer}>
            <Chip
              label="Role Chart"
              color="primary"
              variant="outlined"
              onClick={() => setRoleChartModalOpen(true)}
              className={classes.chip}
            />
          </Box>
        </Grid>

        {isDisabled && (
          <Grid container spacing={2} mt={1} mx={1}>
            <Grid item xs={12} sm={12} md={4}>
              <FormControl
                fullWidth
                error={
                  formik.touched.selectedUser &&
                  Boolean(formik.errors.selectedUser)
                }
              >
                <InputLabel>Select User</InputLabel>
                <Select
                  label="Select User"
                  value={formik.values.selectedUser}
                  onChange={(e) => {
                    formik.setFieldValue("selectedUser", e.target.value);
                    setSelectedUser(e.target.value);
                  }}
                  onBlur={formik.handleBlur}
                  name="selectedUser"
                  readOnly={mode === "edit"}
                >
                  <MenuItem value="" disabled>
                    Select User
                  </MenuItem>
                  {userData.map((data) => (
                    <MenuItem key={data.uuid} value={data.uuid}>
                      {data.username.charAt(0).toUpperCase() +
                        data.username.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.selectedUser && formik.errors.selectedUser && (
                  <Typography color="error">
                    {formik.errors.selectedUser}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <FormControl
                fullWidth
                error={
                  formik.touched.selectedRole &&
                  Boolean(formik.errors.selectedRole)
                }
              >
                <InputLabel>Select Role</InputLabel>
                <Select
                  label="Select Role"
                  value={formik.values.selectedRole}
                  onChange={(e) => {
                    formik.setFieldValue("selectedRole", e.target.value);
                    setSelectedRole(e.target.value);
                  }}
                  onBlur={formik.handleBlur}
                  name="selectedRole"
                >
                  <MenuItem value="" disabled>
                    Select Role
                  </MenuItem>
                  {Array.isArray(rolesData) && rolesData.length > 0 ? (
                    rolesData.map((role) => (
                      <MenuItem
                        key={role.mast_role_uuid}
                        value={role.mast_role_uuid}
                      >
                        {role.mast_role_name.charAt(0).toUpperCase() +
                          role.mast_role_name.slice(1)}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No roles available
                    </MenuItem>
                  )}
                </Select>
                {formik.touched.selectedRole && formik.errors.selectedRole && (
                  <Typography color="error">
                    {formik.errors.selectedRole}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  variant="contained"
                  className={classes.animatedButton}
                  onClick={formik.handleSubmit}
                  style={{ flex: 1, marginTop: "4px" }}
                >
                  {mode === "edit" ? "Update" : "Submit"}
                </Button>
              </div>
            </Grid>
          </Grid>
        )}

        <Grid item xs={12} md={12} mx={1}>
          <CustomDatagrid
            rows={UserListRows}
            columns={columns}
            disableSelectionOnClick={true}
            checkboxSelection={false}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
          />
        </Grid>

        <Grid container justifyContent="space-between" sx={{ mt: 2 }}>
          <Grid item>
            <Button
              variant="outlined"
              style={{ marginTop: "8px" }}
              onClick={onPrevious}
            >
              Previous
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              sx={{ flexGrow: 0 }}
              style={{ marginTop: "8px" }}
              onClick={onNext}
            >
              NEXT
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <UserRoleModal
        open={userRoleManagerOpen}
        onClose={() => setUserRoleManagerOpen(false)}
        onSubmit={handleSubmit}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        isEditing={isEditing}
        setOpen={setUserRoleManagerOpen}
      />
      <RoleChartModal
        open={roleChartModalOpen}
        onClose={() => setRoleChartModalOpen(false)}
      />
      <RoleCreation
        open={customRoleModalOpen}
        onClose={() => setCustomRoleModalOpen(false)}
        onSave={handleCustomRoleSave}
      />
    </>
  );
};

export default UserRoleManager;
